import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../api';
import { PAGE_REGISTRY } from '../nav/pages';
import { useToast } from '../components/Toast';
import { GripVertical } from 'lucide-react';

interface NavModule {
  id: string;
  code: string;
  name: string;
  icon: string | null;
  color: string | null;
  sort_order: number;
  required_org_modules: string[];
  description: string | null;
  page_codes: string[];
  organization_id?: number | null;
  organization_code?: string | null;
}

interface OrgModule { id: number | string; code: string; name: string; description?: string | null }

interface OrgItem {
  id: number;
  short_code: string;
  name: string;
}

const emptyDraft: Omit<NavModule, 'id'> = {
  code: '', name: '', icon: '🧩', color: '#3B82F6', sort_order: 100,
  required_org_modules: [],
  description: null,
  page_codes: [],
  organization_id: null,
};

export default function ModuleContentAdmin() {
  const toast = useToast();
  const [modules, setModules] = useState<NavModule[]>([]);
  const [orgModules, setOrgModules] = useState<OrgModule[]>([]);
  const [orgs, setOrgs] = useState<OrgItem[]>([]);
  const [selectedOrgFilter, setSelectedOrgFilter] = useState<string>('global');
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<NavModule | null>(null);
  const [filter, setFilter] = useState('');
  const [pageViewFilter, setPageViewFilter] = useState<'all' | 'assigned' | 'unassigned'>('all');
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Drag and Drop state
  const [draggedPage, setDraggedPage] = useState<{ code: string; index: number } | null>(null);
  const [dragOverPageIndex, setDragOverPageIndex] = useState<number | null>(null);
  const [dragOverModuleId, setDragOverModuleId] = useState<string | null>(null);

  async function load(targetOrgFilter?: string) {
    setLoading(true);
    try {
      const [mods, oms, orgList] = await Promise.all([
        apiFetch('/admin/nav-modules'),
        apiFetch('/admin/modules').catch(() => []),
        apiFetch('/admin/orgs').catch(() => []),
      ]);
      setModules(mods || []);
      setOrgModules(oms || []);
      setOrgs(orgList || []);

      const orgFilterToUse = targetOrgFilter !== undefined ? targetOrgFilter : selectedOrgFilter;
      
      const filtered = (mods || []).filter((m: NavModule) => {
        if (orgFilterToUse === 'all') return true;
        if (orgFilterToUse === 'global') return m.organization_id === null || !m.organization_code;
        return m.organization_code === orgFilterToUse;
      });

      if (filtered.length > 0) {
        selectModule(filtered[0]);
      } else {
        setSelectedId(null);
        setDraft(null);
      }
    } catch (e: any) {
      toast.error('Fehler beim Laden: ' + (e?.message || e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function handleOrgFilterChange(newFilter: string) {
    setSelectedOrgFilter(newFilter);
    const filtered = modules.filter(m => {
      if (newFilter === 'all') return true;
      if (newFilter === 'global') return m.organization_id === null || !m.organization_code;
      return m.organization_code === newFilter;
    });
    if (filtered.length > 0) {
      selectModule(filtered[0]);
    } else {
      setSelectedId(null);
      setDraft(null);
    }
  }

  function selectModule(m: NavModule) {
    setSelectedId(m.id);
    setDraft({
      ...m,
      page_codes: [...(m.page_codes || [])],
      required_org_modules: [...(m.required_org_modules || [])],
    });
  }

  function newModule() {
    let orgId: number | null = null;
    if (selectedOrgFilter !== 'all' && selectedOrgFilter !== 'global') {
      const foundOrg = orgs.find(o => o.short_code === selectedOrgFilter);
      if (foundOrg) orgId = foundOrg.id;
    }
    setSelectedId('new');
    setDraft({
      id: 'new',
      ...emptyDraft,
      organization_id: orgId,
      page_codes: [],
      required_org_modules: [],
    });
  }

  async function save() {
    if (!draft) return;
    if (!draft.code.trim() || !draft.name.trim()) {
      toast.error('Code und Name sind Pflichtfelder');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        code: draft.code.trim(),
        name: draft.name.trim(),
        icon: draft.icon?.trim() || null,
        color: draft.color || null,
        sort_order: Number(draft.sort_order) || 0,
        required_org_modules: draft.required_org_modules || [],
        description: draft.description || null,
        page_codes: draft.page_codes || [],
        organization_id: draft.organization_id ? Number(draft.organization_id) : null,
      };
      let saved: NavModule;
      if (selectedId && selectedId !== 'new') {
        saved = await apiFetch(`/admin/nav-modules/${selectedId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        await apiFetch(`/admin/nav-modules/${selectedId}/pages`, {
          method: 'PUT',
          body: JSON.stringify({ page_codes: draft.page_codes }),
        });
      } else {
        saved = await apiFetch('/admin/nav-modules', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        if (saved && saved.id) {
          await apiFetch(`/admin/nav-modules/${saved.id}/pages`, {
            method: 'PUT',
            body: JSON.stringify({ page_codes: draft.page_codes }),
          });
        }
      }
      toast.success(`Modul "${draft.name}" gespeichert`);
      await load();
      if (saved && saved.id) setSelectedId(saved.id);
    } catch (e: any) {
      toast.error('Fehler beim Speichern: ' + (e?.message || e));
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!selectedId || selectedId === 'new') return;
    if (!confirm(`Modul "${draft?.name}" wirklich löschen?`)) return;
    try {
      await apiFetch(`/admin/nav-modules/${selectedId}`, { method: 'DELETE' });
      toast.success('Modul gelöscht');
      setSelectedId(null);
      setDraft(null);
      await load();
    } catch (e: any) {
      toast.error('Fehler beim Löschen: ' + (e?.message || e));
    }
  }

  // Copy global standard template to the currently selected organization
  async function handleCopyGlobalToCurrentOrg() {
    if (selectedOrgFilter === 'all' || selectedOrgFilter === 'global') return;
    const currentOrg = orgs.find(o => o.short_code === selectedOrgFilter);
    if (!currentOrg) return;

    if (!confirm(`Möchtest du die System-Standard Navigation (Global) jetzt als bearbeitbare Vorlage für "${currentOrg.name}" übernehmen?`)) {
      return;
    }

    setActionLoading(true);
    try {
      await apiFetch(`/admin/nav-modules/copy-global/${currentOrg.id}`, { method: 'POST' });
      toast.success(`Standard-Navigation für "${currentOrg.name}" erfolgreich übernommen!`);
      await load(selectedOrgFilter);
    } catch (e: any) {
      toast.error('Fehler: ' + (e?.message || e));
    } finally {
      setActionLoading(false);
    }
  }

  // Reset current org to inherit global standard
  async function handleResetOrgToGlobal() {
    if (selectedOrgFilter === 'all' || selectedOrgFilter === 'global') return;
    const currentOrg = orgs.find(o => o.short_code === selectedOrgFilter);
    if (!currentOrg) return;

    if (!confirm(`Möchtest du alle individuellen Module für "${currentOrg.name}" löschen und wieder auf den Standard (Global) zurücksetzen?`)) {
      return;
    }

    setActionLoading(true);
    try {
      await apiFetch(`/admin/nav-modules/reset-org/${currentOrg.id}`, { method: 'POST' });
      toast.success(`"${currentOrg.name}" verwendet jetzt wieder den globalen Standard.`);
      await load(selectedOrgFilter);
    } catch (e: any) {
      toast.error('Fehler: ' + (e?.message || e));
    } finally {
      setActionLoading(false);
    }
  }

  function togglePage(code: string) {
    if (!draft) return;
    const exists = draft.page_codes.includes(code);
    const page_codes = exists
      ? draft.page_codes.filter(c => c !== code)
      : [...draft.page_codes, code];
    setDraft({ ...draft, page_codes });
  }

  function unassignPage(code: string, e?: React.MouseEvent) {
    if (e) e.stopPropagation();
    if (!draft) return;
    setDraft({
      ...draft,
      page_codes: draft.page_codes.filter(c => c !== code),
    });
  }

  // Registry map for quick lookup
  const pageMap = useMemo(() => {
    const map = new Map<string, typeof PAGE_REGISTRY[0]>();
    for (const p of PAGE_REGISTRY) {
      map.set(p.code, p);
    }
    return map;
  }, []);

  // Assigned page items for the current draft
  const assignedPageItems = useMemo(() => {
    if (!draft) return [];
    return draft.page_codes.map(code => {
      const p = pageMap.get(code);
      return {
        code,
        label: p ? p.label : code,
        path: p ? p.path : undefined,
        icon: p?.icon,
      };
    });
  }, [draft, pageMap]);

  // Drag and Drop handlers
  function handlePageDragStart(e: React.DragEvent, code: string, index: number) {
    setDraggedPage({ code, index });
    e.dataTransfer.setData('text/plain', code);
    e.dataTransfer.setData('application/json', JSON.stringify({ code, index, sourceModuleId: selectedId }));
    e.dataTransfer.effectAllowed = 'move';
  }

  function handlePageDragEnd() {
    setDraggedPage(null);
    setDragOverPageIndex(null);
    setDragOverModuleId(null);
  }

  function handlePageDragOver(e: React.DragEvent, index: number) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverPageIndex !== index) {
      setDragOverPageIndex(index);
    }
  }

  function handlePageDragLeave(e: React.DragEvent, index: number) {
    if (dragOverPageIndex === index) {
      setDragOverPageIndex(null);
    }
  }

  async function handlePageDropOnPage(e: React.DragEvent, targetIndex: number) {
    e.preventDefault();
    e.stopPropagation();
    const currentDrag = draggedPage;
    setDraggedPage(null);
    setDragOverPageIndex(null);

    if (!draft || !currentDrag) return;
    if (currentDrag.index === targetIndex) return;

    const updated = [...draft.page_codes];
    const [moved] = updated.splice(currentDrag.index, 1);
    updated.splice(targetIndex, 0, moved);

    setDraft({ ...draft, page_codes: updated });
    setModules(prev => prev.map(m => (m.id === selectedId ? { ...m, page_codes: updated } : m)));

    if (selectedId && selectedId !== 'new') {
      try {
        await apiFetch(`/admin/nav-modules/${selectedId}/pages`, {
          method: 'PUT',
          body: JSON.stringify({ page_codes: updated }),
        });
        toast.success('Seiten-Reihenfolge gespeichert');
      } catch (err: any) {
        toast.error('Fehler beim Speichern der Reihenfolge: ' + (err?.message || err));
      }
    }
  }

  function handleModuleDragOver(e: React.DragEvent, moduleId: string) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverModuleId !== moduleId) {
      setDragOverModuleId(moduleId);
    }
  }

  function handleModuleDragLeave(e: React.DragEvent, moduleId: string) {
    if (dragOverModuleId === moduleId) {
      setDragOverModuleId(null);
    }
  }

  async function handleModuleDrop(e: React.DragEvent, targetModule: NavModule) {
    e.preventDefault();
    e.stopPropagation();
    const currentDrag = draggedPage;
    setDragOverModuleId(null);
    setDraggedPage(null);

    if (!currentDrag) return;
    if (targetModule.id === selectedId) return;

    if (!selectedId || selectedId === 'new') {
      toast.error('Bitte speichere das aktuelle Modul zuerst ab.');
      return;
    }

    const pageCode = currentDrag.code;
    const pInfo = pageMap.get(pageCode);
    const pageLabel = pInfo ? pInfo.label : pageCode;

    const currentModulePages = (draft?.page_codes || []).filter(c => c !== pageCode);
    const targetModulePages = targetModule.page_codes || [];
    const updatedTargetPages = targetModulePages.includes(pageCode)
      ? targetModulePages
      : [...targetModulePages, pageCode];

    // Optimistic UI updates
    setDraft(prev => (prev ? { ...prev, page_codes: currentModulePages } : null));
    setModules(prev =>
      prev.map(m => {
        if (m.id === selectedId) return { ...m, page_codes: currentModulePages };
        if (m.id === targetModule.id) return { ...m, page_codes: updatedTargetPages };
        return m;
      })
    );

    try {
      await Promise.all([
        apiFetch(`/admin/nav-modules/${selectedId}/pages`, {
          method: 'PUT',
          body: JSON.stringify({ page_codes: currentModulePages }),
        }),
        apiFetch(`/admin/nav-modules/${targetModule.id}/pages`, {
          method: 'PUT',
          body: JSON.stringify({ page_codes: updatedTargetPages }),
        }),
      ]);
      toast.success(`Seite "${pageLabel}" in Modul "${targetModule.name}" verschoben`);
    } catch (err: any) {
      toast.error('Fehler beim Verschieben: ' + (err?.message || err));
      await load();
    }
  }

  const filteredPages = useMemo(() => {
    const f = filter.toLowerCase();
    return PAGE_REGISTRY.filter(p => {
      const matchesSearch = !f || p.code.toLowerCase().includes(f) || p.label.toLowerCase().includes(f);
      if (!matchesSearch) return false;
      if (!draft) return true;
      const isAssigned = draft.page_codes.includes(p.code);
      if (pageViewFilter === 'assigned') return isAssigned;
      if (pageViewFilter === 'unassigned') return !isAssigned;
      return true;
    });
  }, [filter, pageViewFilter, draft]);

  const filteredModules = useMemo(() => {
    return modules.filter(m => {
      if (selectedOrgFilter === 'all') return true;
      if (selectedOrgFilter === 'global') return (m as any).organization_id === null || !m.organization_code;
      return m.organization_code === selectedOrgFilter;
    });
  }, [modules, selectedOrgFilter]);

  const selectedOrgInfo = useMemo(() => {
    if (selectedOrgFilter === 'all') return { label: 'Alle Organisationen', isCustom: false };
    if (selectedOrgFilter === 'global') return { label: 'System / Global (Standard)', isCustom: false };
    const org = orgs.find(o => o.short_code === selectedOrgFilter);
    return {
      label: org ? `${org.name} (${org.short_code})` : selectedOrgFilter,
      org,
      isCustom: filteredModules.length > 0,
    };
  }, [selectedOrgFilter, orgs, filteredModules]);

  const usedElsewhere = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const m of filteredModules) {
      if (m.id === selectedId) continue;
      for (const c of m.page_codes) {
        const arr = map.get(c) || [];
        arr.push(m.name);
        map.set(c, arr);
      }
    }
    return map;
  }, [filteredModules, selectedId]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-whitetext-white">Modul-Struktur &amp; Seitenzuordnung</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Strukturiere hier die Navigationsleiste des Portals zentral oder individuell für jede Organisation.
        </p>
        <div className="mt-3 text-sm bg-blue-50 dark:bg-gray-800 border border-blue-200 dark:border-gray-700 rounded-xl p-3.5 text-blue-900 dark:text-gray-200 flex items-center justify-between flex-wrap gap-2 shadow-sm">
          <div className="flex items-center gap-2">
            <span className="text-lg">🛡️</span>
            <span>
              <strong>Berechtigungen &amp; Sichtbarkeiten</strong> werden zentral in der Rollen- &amp; Rechtematrix gesteuert.
            </span>
          </div>
          <a
            href="/admin#roles"
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg shadow-sm transition-colors"
          >
            Rollen &amp; Rechte öffnen →
          </a>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-500 dark:text-gray-400">Lade Navigation…</div>
      ) : (
        <div className="grid grid-cols-12 gap-6">
          {/* Linke Spalte: Modulliste & Organisationsauswahl */}
          <div className="col-span-12 md:col-span-4 lg:col-span-3 space-y-4">
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-900/80">
                <span className="font-semibold text-sm text-gray-800 dark:text-gray-100">Module (Navigation)</span>
                <button
                  onClick={newModule}
                  className="text-xs px-2.5 py-1.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 shadow-sm transition-colors"
                >
                  + Neu
                </button>
              </div>

              {/* Org Selector */}
              <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 space-y-2">
                <label className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider block">
                  Organisation auswählen
                </label>
                <select
                  value={selectedOrgFilter}
                  onChange={(e) => handleOrgFilterChange(e.target.value)}
                  className="w-full text-xs px-2.5 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-whitetext-white font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  <option value="global">🌐 System / Global (Standard)</option>
                  <option value="all">📂 Alle Organisationen anzeigen</option>
                  <optgroup label="Organisationen">
                    {orgs.map(org => {
                      const count = modules.filter(m => m.organization_code === org.short_code).length;
                      return (
                        <option key={org.id} value={org.short_code}>
                          {org.name} ({org.short_code}) {count > 0 ? `[${count} eigene Module]` : `[erbt Standard]`}
                        </option>
                      );
                    })}
                  </optgroup>
                </select>
              </div>

              {/* Status & Quick Action Banner for Org */}
              {selectedOrgFilter !== 'all' && selectedOrgFilter !== 'global' && (
                <div className="p-3 bg-gray-50 dark:bg-gray-900/80 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700 text-xs space-y-2">
                  {filteredModules.length === 0 ? (
                    <div className="space-y-2">
                      <div className="text-amber-700 dark:text-amber-400 font-medium flex items-center gap-1.5">
                        <span>ℹ️</span>
                        <span>Erbt aktuell den globalen Standard ({modules.filter(m => m.organization_id === null).length} Module).</span>
                      </div>
                      <button
                        type="button"
                        onClick={handleCopyGlobalToCurrentOrg}
                        disabled={actionLoading}
                        className="w-full py-1.5 px-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-semibold shadow-xs transition-colors flex items-center justify-center gap-1.5"
                      >
                        📥 Standard hierher kopieren &amp; bearbeiten
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="text-emerald-700 dark:text-emerald-400 font-medium flex items-center justify-between">
                        <span>✓ {filteredModules.length} eigene Module definiert</span>
                      </div>
                      <button
                        type="button"
                        onClick={handleResetOrgToGlobal}
                        disabled={actionLoading}
                        className="w-full py-1.5 px-2 bg-gray-200 dark:bg-gray-700 hover:bg-red-100 hover:text-red-700 dark:bg-gray-700 dark:hover:bg-red-950/50 dark:hover:text-red-300 text-gray-700 dark:text-gray-300 rounded text-xs font-medium transition-colors"
                      >
                        🔄 Auf globalen Standard zurücksetzen
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Module List */}
              <ul className="divide-y divide-gray-100 dark:divide-gray-700/60 max-h-[60vh] overflow-y-auto">
                {filteredModules.length === 0 ? (
                  <li className="p-4 text-center text-xs text-gray-400 dark:text-gray-500 dark:text-gray-400 italic">
                    Keine eigenen Module für diese Organisation angelegt.
                  </li>
                ) : (
                  filteredModules.map(m => {
                    const isSelected = selectedId === m.id;
                    const isDragTarget = dragOverModuleId === m.id;
                    const isSourceModule = selectedId === m.id;
                    return (
                      <li
                        key={m.id}
                        onDragOver={e => handleModuleDragOver(e, m.id)}
                        onDragLeave={e => handleModuleDragLeave(e, m.id)}
                        onDrop={e => handleModuleDrop(e, m)}
                        className="relative"
                      >
                        <button
                          onClick={() => selectModule(m)}
                          className={`w-full text-left px-3.5 py-2.5 flex items-center gap-3 transition-all ${
                            isDragTarget && !isSourceModule
                              ? 'border-2 border-dashed border-blue-500 bg-blue-100/90 dark:bg-blue-900/60 ring-2 ring-blue-400/50 scale-[1.02] shadow-md z-10'
                              : isSelected
                              ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100 border-l-4 border-blue-600 font-semibold'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-700hover:bg-gray-700/50 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          <span className="text-xl shrink-0" style={{ color: m.color || undefined }}>
                            {m.icon && (m.icon.startsWith('/') ? '🖼️' : m.icon)}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 truncate">
                              <span className="text-sm font-medium">{m.name}</span>
                              {m.organization_code && (
                                <span className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-900/30">
                                  {m.organization_code}
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-gray-400 dark:text-gray-500 dark:text-gray-400 truncate">
                              {isDragTarget && !isSourceModule ? (
                                <span className="text-blue-700 dark:text-blue-300 font-semibold flex items-center gap-1">
                                  📥 Hier ablegen
                                </span>
                              ) : (
                                m.page_codes.length === 1 ? '1 Seite zugeteilt' : `${m.page_codes.length} Seiten zugeteilt`
                              )}
                            </div>
                          </div>
                          <span
                            className={`text-xs font-mono px-2 py-0.5 rounded-full transition-colors ${
                              isDragTarget && !isSourceModule
                                ? 'bg-blue-600 text-white font-bold animate-pulse'
                                : 'text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800/60'
                            }`}
                          >
                            {isDragTarget && !isSourceModule && draggedPage && !m.page_codes.includes(draggedPage.code)
                              ? `+1 (${m.page_codes.length + 1})`
                              : m.page_codes.length}
                          </span>
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
          </div>

          {/* Rechte Spalte: Formular & Seitenauswahl */}
          <div className="col-span-12 md:col-span-8 lg:col-span-9">
            {!draft ? (
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm p-12 text-center text-gray-500 dark:text-gray-400">
                <span className="text-4xl block mb-2">🧩</span>
                <h3 className="font-semibold text-gray-900 dark:text-whitetext-white mb-1">
                  Kein Modul ausgewählt
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-4">
                  {selectedOrgFilter !== 'all' && selectedOrgFilter !== 'global' && filteredModules.length === 0
                    ? `Für "${selectedOrgInfo.label}" existieren noch keine eigenen Module. Klicke links auf "Standard hierher kopieren" oder lege ein neues an.`
                    : 'Wähle links ein Modul aus der Liste aus oder klicke auf "+ Neu", um ein Modul zu erstellen.'}
                </p>
                {selectedOrgFilter !== 'all' && selectedOrgFilter !== 'global' && filteredModules.length === 0 && (
                  <button
                    onClick={handleCopyGlobalToCurrentOrg}
                    disabled={actionLoading}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg shadow-sm transition-colors"
                  >
                    📥 Standard-Navigation als Vorlage kopieren
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                {/* Modul-Stammdaten */}
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm p-5 space-y-4">
                  <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{draft.icon && (draft.icon.startsWith('/') ? '🖼️' : draft.icon)}</span>
                      <div>
                        <h2 className="text-base font-bold text-gray-900 dark:text-whitetext-white">
                          {draft.name ? draft.name : 'Neues Modul'}
                        </h2>
                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                          <span className="font-mono">Code: {draft.code || '–'}</span>
                          <span>•</span>
                          <span>
                            Geltungsbereich:{' '}
                            <strong className="text-gray-700 dark:text-gray-300">
                              {draft.organization_id
                                ? orgs.find(o => o.id === draft.organization_id)?.name || `Org ID ${draft.organization_id}`
                                : 'Global (System-Standard)'}
                            </strong>
                          </span>
                        </div>
                      </div>
                    </div>
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300">
                      {draft.page_codes.length} Seiten zugeteilt
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Field label="Code (eindeutig)">
                      <input
                        value={draft.code}
                        onChange={e => setDraft({ ...draft, code: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-whitetext-white text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        placeholder="z.B. dienstplan"
                      />
                    </Field>

                    <Field label="Name (Anzeige in Navigation)">
                      <input
                        value={draft.name}
                        onChange={e => setDraft({ ...draft, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-whitetext-white text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        placeholder="z.B. Dienstplan"
                      />
                    </Field>

                    <Field label="Geltungsbereich (Organisation)">
                      <select
                        value={draft.organization_id ? String(draft.organization_id) : 'global'}
                        onChange={e => {
                          const val = e.target.value === 'global' ? null : Number(e.target.value);
                          setDraft({ ...draft, organization_id: val });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-whitetext-white text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      >
                        <option value="global">🌐 Global (System-Standard)</option>
                        {orgs.map(org => (
                          <option key={org.id} value={org.id}>
                            🏢 {org.name} ({org.short_code})
                          </option>
                        ))}
                      </select>
                    </Field>

                    <Field label="Icon (Emoji oder Bildpfad)">
                      <input
                        value={draft.icon || ''}
                        onChange={e => setDraft({ ...draft, icon: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-whitetext-white text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        placeholder="📅 oder /icons/plan.png"
                      />
                    </Field>

                    <Field label="Farbe">
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={draft.color || '#3B82F6'}
                          onChange={e => setDraft({ ...draft, color: e.target.value })}
                          className="w-10 h-9 p-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 cursor-pointer"
                        />
                        <span className="text-xs font-mono text-gray-600 dark:text-gray-400">
                          {draft.color || '#3B82F6'}
                        </span>
                      </div>
                    </Field>

                    <Field label="Sortierungs-Reihenfolge">
                      <input
                        type="number"
                        value={draft.sort_order}
                        onChange={e => setDraft({ ...draft, sort_order: Number(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-whitetext-white text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      />
                    </Field>

                    <div className="md:col-span-2 lg:col-span-3">
                      <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                        Erforderliche SaaS-Module (Sichtbarkeit für Organisationen)
                      </label>
                      <div className="flex flex-wrap gap-2 p-2.5 bg-gray-50 dark:bg-gray-900/60 rounded-lg border border-gray-200 dark:border-gray-700 max-h-40 overflow-y-auto">
                        {orgModules.length === 0 ? (
                          <span className="text-xs text-gray-400">Lade Module...</span>
                        ) : (
                          orgModules.map(om => {
                            const isChecked = (draft.required_org_modules || []).includes(om.code);
                            return (
                              <label
                                key={om.code}
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs cursor-pointer border transition-colors ${
                                  isChecked
                                    ? 'bg-blue-50 dark:bg-blue-900/40 border-blue-300 dark:border-blue-600 text-blue-800 dark:text-blue-200 font-medium'
                                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700hover:bg-gray-600'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={e => {
                                    const current = draft.required_org_modules || [];
                                    const updated = e.target.checked
                                      ? [...current, om.code]
                                      : current.filter(c => c !== om.code);
                                    setDraft({ ...draft, required_org_modules: updated });
                                  }}
                                  className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 w-3.5 h-3.5"
                                />
                                <span>{om.name}</span>
                                <span className="text-[10px] text-gray-400 font-mono">({om.code})</span>
                              </label>
                            );
                          })
                        )}
                      </div>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                        Wenn ausgewählt, wird dieses Navigationsmodul nur Organisationen angezeigt, die diese SaaS-Module in der Modulverwaltung freigeschaltet haben (z. B. <code className="text-blue-600 dark:text-blue-400 font-mono">zeiterfassung</code>, <code className="text-blue-600 dark:text-blue-400 font-mono">dienstplan</code>). Leer = für alle freigeschaltet.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
                    <div>
                      {selectedId && selectedId !== 'new' && (
                        <button
                          type="button"
                          onClick={remove}
                          className="px-3.5 py-1.5 text-xs bg-red-50 hover:bg-red-100 text-red-600 dark:bg-red-900/20 dark:hover:bg-red-900/30 dark:text-red-300 font-medium rounded-lg border border-red-200 dark:border-red-800 transition-colors"
                        >
                          🗑️ Modul löschen
                        </button>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={save}
                      disabled={saving}
                      className="px-5 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-sm disabled:opacity-50 transition-colors flex items-center gap-2"
                    >
                      {saving ? 'Speichert…' : '💾 Modul speichern'}
                    </button>
                  </div>
                </div>

                {/* Sektion 1: Aktuell zugeteilte Seiten (Direkte Übersicht) */}
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm overflow-hidden">
                  <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-blue-50/50 dark:bg-gray-800/90">
                    <div className="flex items-center gap-2">
                      <span className="text-base">📋</span>
                      <div>
                        <h3 className="font-semibold text-sm text-gray-900 dark:text-whitetext-white">
                          Aktuell zugeteilte Seiten für &quot;{draft.name || draft.code || 'Modul'}&quot;
                        </h3>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400">
                          Per Drag &amp; Drop sortieren oder nach links auf ein anderes Modul ziehen
                        </p>
                      </div>
                    </div>
                    <span className="text-xs font-bold text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/50 px-2.5 py-1 rounded-full shrink-0">
                      {assignedPageItems.length} aktiv
                    </span>
                  </div>

                  <div className="p-4">
                    {assignedPageItems.length === 0 ? (
                      <div className="text-center py-6 text-sm text-gray-400 dark:text-gray-500 dark:text-gray-400 italic bg-gray-50 dark:bg-gray-900/40 rounded-lg border border-dashed border-gray-200 dark:border-gray-700">
                        Diesem Modul sind noch keine Seiten zugeteilt. Wähle unten Seiten aus der Liste aus.
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
                        {assignedPageItems.map((p, idx) => {
                          const isBeingDragged = draggedPage?.code === p.code;
                          const isDragOver = dragOverPageIndex === idx && !isBeingDragged;

                          return (
                            <div
                              key={p.code}
                              draggable={true}
                              onDragStart={e => handlePageDragStart(e, p.code, idx)}
                              onDragOver={e => handlePageDragOver(e, idx)}
                              onDragLeave={e => handlePageDragLeave(e, idx)}
                              onDrop={e => handlePageDropOnPage(e, idx)}
                              onDragEnd={handlePageDragEnd}
                              className={`group relative flex items-center justify-between p-2.5 rounded-lg border transition-all select-none cursor-grab active:cursor-grabbing ${
                                isBeingDragged
                                  ? 'opacity-30 border-dashed border-blue-500 scale-95 bg-blue-50/50 dark:bg-blue-900/10'
                                  : isDragOver
                                  ? 'border-blue-500 bg-blue-100/70 dark:bg-blue-900/40 ring-2 ring-blue-500 shadow-md scale-[1.02]'
                                  : 'bg-gray-50 dark:bg-gray-900/50 hover:bg-blue-50/60 dark:hover:bg-blue-900/20 border-gray-200 dark:border-gray-700'
                              }`}
                              title="Ziehen zum Sortieren oder nach links auf ein Modul ziehen zum Verschieben"
                            >
                              <div className="flex items-center gap-2 min-w-0 pr-2">
                                <GripVertical className="w-4 h-4 text-gray-400 dark:text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors shrink-0" />
                                <span className="text-sm shrink-0">📄</span>
                                <div className="min-w-0">
                                  <div className="text-xs font-semibold text-gray-900 dark:text-whitetext-white truncate" title={p.label}>
                                    {idx + 1}. {p.label}
                                  </div>
                                  <div className="text-[10px] text-gray-400 dark:text-gray-500 dark:text-gray-400 font-mono truncate">
                                    {p.code}
                                  </div>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={e => unassignPage(p.code, e)}
                                className="text-gray-400 hover:text-red-600 dark:text-gray-500 dark:text-gray-400hover:text-red-400 text-xs p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors shrink-0"
                                title="Aus diesem Modul entfernen"
                              >
                                ✕
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Sektion 2: Alle Seiten zum Zuweisen / Verwalten */}
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm overflow-hidden">
                  <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-gray-50 dark:bg-gray-900/80">
                    <div>
                      <span className="font-semibold text-sm text-gray-900 dark:text-whitetext-white">Alle verfügbaren Portal-Seiten</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                        Klicke eine Seite an, um sie hinzuzufügen oder zu entfernen
                      </span>
                    </div>

                    <div className="flex items-center gap-2 w-full sm:w-auto">
                      {/* Filter-Tabs */}
                      <div className="flex items-center bg-gray-200 dark:bg-gray-700 p-0.5 rounded-lg text-xs">
                        <button
                          type="button"
                          onClick={() => setPageViewFilter('all')}
                          className={`px-2 py-1 rounded-md font-medium transition-colors ${
                            pageViewFilter === 'all'
                              ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-whitetext-white shadow-xs'
                              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-white'
                          }`}
                        >
                          Alle ({PAGE_REGISTRY.length})
                        </button>
                        <button
                          type="button"
                          onClick={() => setPageViewFilter('assigned')}
                          className={`px-2 py-1 rounded-md font-medium transition-colors ${
                            pageViewFilter === 'assigned'
                              ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-whitetext-white shadow-xs'
                              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-white'
                          }`}
                        >
                          Zugeordnet ({draft.page_codes.length})
                        </button>
                        <button
                          type="button"
                          onClick={() => setPageViewFilter('unassigned')}
                          className={`px-2 py-1 rounded-md font-medium transition-colors ${
                            pageViewFilter === 'unassigned'
                              ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-whitetext-white shadow-xs'
                              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-white'
                          }`}
                        >
                          Nicht zugeteilt ({PAGE_REGISTRY.length - draft.page_codes.length})
                        </button>
                      </div>

                      <input
                        value={filter}
                        onChange={e => setFilter(e.target.value)}
                        placeholder="🔍 Suchen…"
                        className="w-full sm:w-44 px-3 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-whitetext-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="max-h-[50vh] overflow-y-auto p-4 space-y-2">
                    {filteredPages.map(p => {
                      const checked = draft.page_codes.includes(p.code);
                      const others = usedElsewhere.get(p.code) || [];
                      return (
                        <div
                          key={p.code}
                          onClick={() => togglePage(p.code)}
                          className={`rounded-xl border transition-all p-3 cursor-pointer select-none flex items-center justify-between gap-3 ${
                            checked
                              ? 'border-blue-500 bg-blue-50/70 dark:bg-blue-900/20 shadow-xs ring-1 ring-blue-500/20'
                              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:border-gray-600hover:border-gray-600'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {}}
                              className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 cursor-pointer pointer-events-none"
                            />
                            <div>
                              <span className="text-sm font-medium text-gray-900 dark:text-whitetext-white">{p.label}</span>
                              <span className="text-xs text-gray-400 dark:text-gray-500 dark:text-gray-400 font-mono ml-2">({p.code})</span>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            {checked && (
                              <span className="text-[11px] font-semibold text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/40 px-2 py-0.5 rounded">
                                ✓ Zugeordnet
                              </span>
                            )}
                            {others.length > 0 && (
                              <span
                                className="text-[11px] font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 rounded-full border border-amber-200 dark:border-amber-800/40"
                                title={'Ebenfalls enthalten in: ' + others.join(', ')}
                              >
                                auch in {others.join(', ')}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 block">{label}</span>
      <div>{children}</div>
    </label>
  );
}
